import { useCollapsibleGroups } from "../lib/useCollapsibleGroups";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { fmtDate } from "../lib/formatters";
import { PhoneLink, EmailLink } from "../lib/formatters";
import { Upload, Trash2, Plus, X, MessageCircle, FileSpreadsheet, Users, Check, Pencil, User, History, Mail } from "lucide-react";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import UploadPreviewModal from "../components/UploadPreviewModal";
import ArrivalHistoryModal from "../components/ArrivalHistoryModal";
import BulkActionBar from "../components/BulkActionBar";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import DestCityFilter from "../components/DestCityFilter";

// Clean a phone number for the wa.me URL: strip everything except digits.
const cleanPhone = (s) => (s || "").replace(/[^0-9]/g, "");

// Airport-code / city-name aliases → canonical city. Keeps arrival groups
// deduped when partners type "JAI", "JAIPUR", "Jaipur" or "jaipur" in the
// same batch. Add codes as new destinations come online.
const CITY_ALIASES = {
  // India
  jai: "Jaipur", jaipur: "Jaipur",
  del: "Delhi", delhi: "Delhi", dli: "Delhi",
  bom: "Mumbai", mumbai: "Mumbai", bombay: "Mumbai",
  blr: "Bangalore", bangalore: "Bangalore", bengaluru: "Bangalore",
  maa: "Chennai", chennai: "Chennai", madras: "Chennai",
  ccu: "Kolkata", kolkata: "Kolkata", calcutta: "Kolkata",
  hyd: "Hyderabad", hyderabad: "Hyderabad",
  goi: "Goa", goa: "Goa", gox: "Goa",
  cok: "Kochi", kochi: "Kochi", cochin: "Kochi",
  udr: "Udaipur", udaipur: "Udaipur",
  jdh: "Jodhpur", jodhpur: "Jodhpur",
  agr: "Agra", agra: "Agra",
  amd: "Ahmedabad", ahmedabad: "Ahmedabad",
  pnq: "Pune", pune: "Pune",
  // Nepal
  ktm: "Kathmandu", kathmandu: "Kathmandu",
  pkr: "Pokhara", pokhara: "Pokhara",
  // Sri Lanka
  cmb: "Colombo", colombo: "Colombo",
  // Africa
  cpt: "Cape Town", "cape town": "Cape Town", capetown: "Cape Town",
  jnb: "Johannesburg", johannesburg: "Johannesburg",
  ebb: "Entebbe", entebbe: "Entebbe",
  nbo: "Nairobi", nairobi: "Nairobi",
  los: "Lagos", lagos: "Lagos",
  // South America
  lpb: "La Paz", "la paz": "La Paz", lapaz: "La Paz",
  // South East Asia
  bkk: "Bangkok", bangkok: "Bangkok",
  han: "Hanoi", hanoi: "Hanoi",
  sgn: "Ho Chi Minh City", "ho chi minh": "Ho Chi Minh City", saigon: "Ho Chi Minh City",
};
// Title-case fallback for unknown values ("kandy" → "Kandy") so the display
// stays clean even without an explicit alias entry.
const titleCase = (s) => (s || "")
  .split(/\s+/).filter(Boolean)
  .map(w => w[0].toUpperCase() + w.slice(1).toLowerCase())
  .join(" ");
const normalizeArrivalCity = (raw) => {
  const k = (raw || "").trim();
  if (!k) return "";
  const alias = CITY_ALIASES[k.toLowerCase()];
  if (alias) return alias;
  return titleCase(k);
};

// Small circular avatar for a participant. Falls back to first-letter
// initial when the participant hasn't uploaded a profile pic yet.
function ParticipantAvatar({ picId, name, size = 24 }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  if (picId) {
    return (
      <img src={`${API}/files/${picId}?auth=${getToken()}`} alt=""
           className="rounded-full object-cover border border-[color:var(--aii-border)] flex-shrink-0"
           style={{ width: size, height: size }}/>
    );
  }
  return (
    <div className="rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center flex-shrink-0"
         style={{ width: size, height: size, fontSize: Math.max(10, size * 0.42) }}>{initial}</div>
  );
}

// Build the WhatsApp message the pickup contact will receive.
const buildMessage = (r, fromName) => {
  const lines = [
    `Namaste 🙏`,
    ``,
    `${fromName || "Aii Group"} team here — could you please pick up the following Aii Group participant?`,
    ``,
    `Participant: ${r.customer || "-"}`,
  ];
  if (r.programme) lines.push(`Programme: ${r.programme}`);
  if (r.arrival_type) lines.push(`Arrival by: ${r.arrival_type}`);
  if (r.flight_date) lines.push(`Arrival date: ${r.flight_date}`);
  if (r.flight_time) lines.push(`Time: ${r.flight_time}`);
  if (r.airline || r.flight_number) lines.push(`Flight: ${[r.airline, r.flight_number].filter(Boolean).join(" · ")}`);
  if (r.hotel_name) lines.push(`Drop at: ${r.hotel_name}${r.hotel_address ? ` — ${r.hotel_address}` : ""}`);
  if (r.hotel_pickup_time) lines.push(`Hotel pickup time: ${r.hotel_pickup_time}`);
  lines.push(``, `Please confirm on receipt. Thank you!`);
  return lines.join("\n");
};

// Compact relative-time formatter for pickup audit lines — "5m ago",
// "2h ago", "3d ago". Falls back to the raw ISO for anything > 90 days
// so audit lines from months back still make sense to Founders.
const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 90) return `${d}d ago`;
  return iso.slice(0, 10);
};

// Reusable audit trail chip — used inside both the locked card and the
// Founder-editable branch of the pickup cell.
const PickupAudit = ({ row }) => {
  if (!row.pickup_updated_by_name && !row.pickup_updated_at) return null;
  return (
    <div className="text-[10px] text-[color:var(--aii-muted)] mt-1 flex items-center gap-1 whitespace-nowrap"
         data-testid={`arr-pickup-audit-${row.id}`}
         title={row.pickup_updated_at ? new Date(row.pickup_updated_at).toLocaleString() : ""}>
      <History size={9}/>
      <span>edited by <b className="text-[color:var(--aii-text-2)]">{row.pickup_updated_by_name || "team"}</b>
        {row.pickup_updated_by_role ? <span className="text-[9px] uppercase tracking-widest ml-1 opacity-70">· {row.pickup_updated_by_role}</span> : null}
        {row.pickup_updated_at ? <span> · {relTime(row.pickup_updated_at)}</span> : null}
      </span>
    </div>
  );
};

export default function Arrivals() {
  const { user } = useAuth();
  const canDelete = (user?.real_role || user?.effective_role) === "FOUNDER";
  const [rows, setRows] = useState([]);
  const [team, setTeam] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});
  const [filter, setFilter] = useState({ destination: "", city: "" });
  const [partnerFilter, setPartnerFilter] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("self"); // "self" | "other"
  const [bulkForm, setBulkForm] = useState({ user_id: "", pickup_name: "", pickup_phone: "" });
  const [bulkBusy, setBulkBusy] = useState(false);
  // Track rows that just got their pickup saved so the row briefly pulses
  // green as visible confirmation.  Cleared automatically after ~1s so the
  // highlight doesn't linger and pull the eye.
  const [flashRows, setFlashRows] = useState(() => new Set());
  const flashRow = (id) => {
    setFlashRows(prev => new Set([...prev, id]));
    setTimeout(() => setFlashRows(prev => { const n = new Set(prev); n.delete(id); return n; }), 900);
  };
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("aii-groupby:arrivals") || "city"); // "city" | "month" | "none"
  const [tab, setTab] = useState("active"); // active | past — participants whose flight_date is before today land in Past
  const { collapsed: collapsedGroups, toggle: toggleGroup } = useCollapsibleGroups("arrivals");
  const fileRef = useRef();

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const [a, u] = await Promise.all([api.get(`/arrivals?${q.toString()}`), api.get("/users").catch(()=>({data:[]}))]);
    // Ascending by flight date (soonest first) — user preference
    const sorted = [...a.data].sort((x,y) => (x.flight_date||"").localeCompare(y.flight_date||""));
    setRows(sorted);
    // Keep Founder in the team lookup so a Founder-assigned self pickup
    // still resolves to a display name below (previously `team.find(...)`
    // came up empty when Founder assigned to themselves and the row fell
    // back to "Team member"). We hide Founders from the *bulk-assign*
    // driver dropdown separately (see `drivers` below).
    setTeam(u.data);
  };
  useEffect(() => { load(); }, [filter.destination, filter.city]);

  // Past/active split — a flight_date strictly before today becomes Past;
  // rows with no flight_date remain in Active so nothing hides silently.
  const today = new Date().toISOString().slice(0, 10);
  const isPast = (r) => (r.flight_date || "") && r.flight_date < today;
  const visibleRows = rows.filter(r => {
    if (partnerFilter && (r.partner_name || "") !== partnerFilter) return false;
    return tab === "past" ? isPast(r) : !isPast(r);
  });
  const pastCount = rows.filter(isPast).length;
  // Unique partner names in the loaded set (excluding blanks) — used for
  // the top-of-page partner filter so BDOM can zoom in on one partner's
  // incoming week without scrolling.
  const partnerOptions = useMemo(() => {
    const set = new Set();
    for (const r of rows) if (r.partner_name) set.add(r.partner_name);
    return [...set].sort();
  }, [rows]);

  // Only GEHM and Founder can pick/save/edit the pickup person on an
  // arrival. BDOM + PCWM lose the "Assign to" column — they still see
  // the resolved read-only card once a pickup is set (via `locked`).
  const canAssign = ["FOUNDER","GEHM"].includes(user.effective_role);

  // Only GEHMs/BDOMs are legitimate self-pickup targets — Founder-role users
  // rarely drive to the airport themselves.
  const drivers = useMemo(() => team.filter(u => ["GEHM", "BDOM"].includes(u.role)), [team]);

  const toggleSelect = useCallback((id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }), []);
  const selectAllVisible = useCallback(() => setSelected(prev =>
    (rows.length > 0 && rows.every(r => prev.has(r.id))) ? new Set() : new Set(rows.map(r => r.id))
  ), [rows]);
  const clearSelected = useCallback(() => setSelected(new Set()), []);

  const applyBulkPickup = async () => {
    if (selected.size === 0) return toast.error("Select at least one arrival");
    const ids = [...selected];
    let body = {};
    if (bulkMode === "self") {
      if (!bulkForm.user_id) return toast.error("Pick a team member to assign");
      body = { pickup_mode: "self", assigned_user_id: bulkForm.user_id, pickup_name: "", pickup_phone: "" };
    } else {
      if (!bulkForm.pickup_name.trim()) return toast.error("Enter the driver's name");
      body = { pickup_mode: "other", pickup_name: bulkForm.pickup_name.trim(), pickup_phone: bulkForm.pickup_phone.trim(), assigned_user_id: null };
    }
    setBulkBusy(true);
    try {
      await Promise.all(ids.map(id => api.patch(`/arrivals/${id}`, body)));
      const who = bulkMode === "self"
        ? (drivers.find(d => d.id === bulkForm.user_id)?.name || "team member")
        : bulkForm.pickup_name.trim();
      toast.success(`Pickup assigned to ${who} for ${ids.length} arrival${ids.length === 1 ? "" : "s"}`);
      setBulkOpen(false);
      setBulkForm({ user_id: "", pickup_name: "", pickup_phone: "" });
      clearSelected();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk assign failed"); }
    finally { setBulkBusy(false); }
  };

  const add = async () => {
    if (!form.customer) return toast.error("Customer required");
    await api.post("/arrivals", form);
    setForm({}); setAdding(false); await load(); toast.success("Added");
  };
  const del = async (id) => { await api.delete(`/arrivals/${id}`); await load(); };
  const [delTarget, setDelTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  // Reuses the existing `selected` set (backing the bulk-pickup-assign
  // flow) so users have one selection state per page. Founder-only.
  const bulkDelete = async () => {
    const ids = [...selected].filter(Boolean);
    await Promise.all(ids.map(id => api.delete(`/arrivals/${id}`)));
    setSelected(new Set());
    await load();
  };
  const patch = async (id, body) => { await api.patch(`/arrivals/${id}`, body); await load(); };

  const sendWA = (r) => {
    const phone = cleanPhone(r.pickup_phone);
    if (!phone) return toast.error("Add pickup WhatsApp number first");
    const msg = encodeURIComponent(buildMessage(r, user.name));
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  const [pendingUpload, setPendingUpload] = useState(null);
  const upload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPendingUpload(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/arrivals/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "aii-arrivals-template.xlsx";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not download template");
    }
  };

  const FIELDS = [
    { k:"customer", label:"Customer" }, { k:"partner_name", label:"Partner" }, { k:"programme", label:"Programme" }, { k:"destination", label:"Destination" },
    { k:"start_date", label:"Start date", t:"date" }, { k:"end_date", label:"End date", t:"date" },
    { k:"arrival_type", label:"Arrival type", options:["Airport","Train","Bus","Self"] },
    { k:"flight_date", label:"Flight date", t:"date" }, { k:"flight_time", label:"Flight time", t:"time" },
    { k:"airline", label:"Airline" }, { k:"flight_number", label:"Flight #" },
    { k:"hotel_name", label:"Hotel name" }, { k:"hotel_address", label:"Hotel address" },
    { k:"hotel_pickup_time", label:"Hotel pickup", t:"time" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Arrival Information Overview</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <DestCityFilter value={filter} onChange={setFilter} />
          {partnerOptions.length > 0 && (
            <select
              data-testid="arr-partner-filter"
              value={partnerFilter}
              onChange={e => setPartnerFilter(e.target.value)}
              title="Filter arrivals by sending partner"
              className="text-xs px-2.5 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white">
              <option value="">All partners</option>
              {partnerOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          <div className="inline-flex rounded-md border border-[color:var(--aii-border)] overflow-hidden" data-testid="arr-tabs">
            <button data-testid="arr-tab-active" onClick={()=>setTab("active")}
                    className={`px-3 py-1.5 text-sm font-semibold ${tab === "active" ? "bg-brand text-white" : "bg-white text-[color:var(--aii-text-2)] hover:bg-slate-50"}`}>
              Upcoming
            </button>
            <button data-testid="arr-tab-past" onClick={()=>setTab("past")}
                    title="Arrivals whose flight date is before today"
                    className={`px-3 py-1.5 text-sm font-semibold ${tab === "past" ? "bg-brand text-white" : "bg-white text-[color:var(--aii-text-2)] hover:bg-slate-50"}`}>
              Past {pastCount > 0 && <span className="ml-1 text-[10px] opacity-70">({pastCount})</span>}
            </button>
          </div>
          <div className="inline-flex rounded-md border border-[color:var(--aii-border)] overflow-hidden" data-testid="arr-groupby">
            {["city","month","none"].map(g => (
              <button key={`arr-gb-${g}`} data-testid={`arr-groupby-${g}`}
                      onClick={() => { setGroupBy(g); try { localStorage.setItem("aii-groupby:arrivals", g); } catch { /* ignore */ } }}
                      title={g === "none" ? "Flat list" : `Group by ${g}`}
                      className={`px-2.5 py-1.5 text-xs font-semibold ${groupBy === g ? "bg-brand text-white" : "bg-white text-[color:var(--aii-text-2)] hover:bg-slate-50"}`}>
                {g === "city" ? "City" : g === "month" ? "Month" : "None"}
              </button>
            ))}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={upload} className="hidden"/>
          <button type="button" data-testid="arr-template" onClick={downloadTemplate}
            className="text-sm px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5"
            title="Download the pre-formatted Excel template">
            <FileSpreadsheet size={14}/> Template
          </button>
          <button onClick={() => fileRef.current?.click()} className="text-sm px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5"><Upload size={14}/> Upload</button>
          <button data-testid="arr-add" onClick={()=>setAdding(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5"><Plus size={14}/> Add row</button>
        </div>
      </div>

      {adding && (
        <div className="aii-card p-5">
          <div className="flex justify-between items-center mb-3"><h3 className="font-semibold">New arrival</h3><button onClick={()=>setAdding(false)}><X size={16}/></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FIELDS.map(f => (
              <div key={f.k}>
                <label className="text-xs font-semibold text-[color:var(--aii-text-2)] uppercase tracking-wider">{f.label}</label>
                {f.options ? (
                  <select value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))}
                    className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                    <option value="">Select…</option>{f.options.map(o=><option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.t||"text"} value={form[f.k]||""} onChange={e=>setForm(v=>({...v,[f.k]:e.target.value}))}
                    className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-4"><button data-testid="arr-save" onClick={add} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark">Save</button></div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        {canDelete && (
          <BulkActionBar count={selected.size} label="arrival"
            onClear={() => setSelected(new Set())}
            onDelete={bulkDelete}/>
        )}
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr>
              {canAssign && (
                <th className="w-8">
                  <input type="checkbox" data-testid="arr-select-all"
                         checked={rows.length > 0 && rows.every(r => selected.has(r.id))}
                         onChange={selectAllVisible}
                         title="Select all visible arrivals for bulk pickup assign"/>
                </th>
              )}
              <th>Customer</th><th>Sex</th><th>Partner</th><th>Programme</th><th>Destination</th><th>City</th><th>Airport</th><th>Flight date</th><th>Time</th><th>Airline</th><th>Flight #</th>
              <th>Hotel</th><th>Pickup</th><th>Pickup by</th><th></th>
            </tr></thead>
            <tbody>
              {visibleRows.length===0 && <tr><td colSpan={canAssign?17:16} className="text-center py-10 text-[color:var(--aii-muted)]">{tab === "past" ? "No past arrivals." : "No upcoming arrivals."}</td></tr>}
              {(() => {
                // Multi-mode grouping.  City mode keeps the 2-level City → Date
                // structure (best for driver rosters).  Month mode groups all
                // arrivals by flight-date month (best for horizon planning).
                // None keeps the raw table.
                const cols = canAssign ? 17 : 16;
                const out = [];
                const rowJsx = (r) => (
                  <tr key={r.id} data-testid={`arr-row-${r.id}`} className={`${flashRows.has(r.id) ? "bg-emerald-100 transition-colors duration-700" : selected.has(r.id) ? "bg-[color:var(--aii-green-tint)]/40" : ""}`}>
                    {canAssign && (
                      <td>
                        <input type="checkbox" data-testid={`arr-select-${r.id}`}
                               checked={selected.has(r.id)}
                               onChange={() => toggleSelect(r.id)}/>
                      </td>
                    )}
                    <td className="font-medium">
                      <div className="flex items-start gap-2">
                        <ParticipantAvatar picId={r.profile_pic_id} name={r.customer} size={26}/>
                        <div className="flex flex-col gap-0.5">
                          <span data-testid={`arr-name-${r.id}`}>{r.customer}</span>
                          {/* Universal contact strip — every role scoped to
                              this destination sees clickable email + WA. */}
                          {((r.email && !r.email.toLowerCase().endsWith("@participants.aiigroup.com")) || r.telephone || r.whatsapp) && (
                            <div className="flex items-center gap-2 flex-wrap text-[11px] font-normal">
                              {r.email && !r.email.toLowerCase().endsWith("@participants.aiigroup.com") && (
                                <span data-testid={`arr-email-${r.id}`} className="inline-flex items-center gap-1">
                                  <Mail size={10} className="text-[color:var(--aii-muted)]"/>
                                  <EmailLink value={r.email}/>
                                </span>
                              )}
                              {(r.whatsapp || r.telephone) && (
                                <span data-testid={`arr-phone-${r.id}`} className="inline-flex items-center gap-1">
                                  <MessageCircle size={10} className="text-[#25D366]"/>
                                  <PhoneLink value={r.whatsapp || r.telephone}/>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-center" data-testid={`arr-gender-${r.id}`}>
                      {(() => {
                        // Colour-coded M/F/O pill mirrors the Weekend
                        // Bookings badge so GEHM / drivers can spot
                        // room-share / accommodation needs at a glance.
                        const g = String(r.gender || "").trim().toLowerCase();
                        if (!g || g === "unknown" || g === "prefer_not_to_say") return <span className="text-[color:var(--aii-muted)] text-xs">—</span>;
                        const cfg = (g === "male" || g === "m") ? { l: "M", cls: "bg-blue-100 text-blue-700" }
                                  : (g === "female" || g === "f") ? { l: "F", cls: "bg-pink-100 text-pink-700" }
                                  : { l: "O", cls: "bg-purple-100 text-purple-700" };
                        return <span title={`Gender: ${g}`} className={`inline-flex items-center justify-center text-[10px] font-bold w-5 h-5 rounded-full ${cfg.cls}`}>{cfg.l}</span>;
                      })()}
                    </td>
                    <td className="text-xs" data-testid={`arr-partner-${r.id}`}>{r.partner_name || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                    <td>{r.programme||"—"}</td>
                    <td className="text-xs whitespace-nowrap">{r.destination||"—"}</td>
                    <td className="text-xs whitespace-nowrap">{r.city||"—"}</td>
                    <td className="text-xs whitespace-nowrap font-semibold" data-testid={`arr-airport-${r.id}`}>{r.arrival_airport||"—"}</td>
                    <td>{fmtDate(r.flight_date)||"—"}
                      {/* Countdown chip — visible to every role so the
                          whole team feels the same urgency. Amber inside
                          48h, red inside 12h. */}
                      {r.arrives_in_hours != null && r.arrives_in_hours > 0 && (
                        <span data-testid={`arr-countdown-${r.id}`}
                              className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${
                                r.arrives_in_hours <= 12 ? "bg-red-100 text-red-700"
                                : r.arrives_in_hours <= 48 ? "bg-amber-100 text-amber-800"
                                : "bg-[color:var(--aii-green-tint)] text-brand"
                              }`}>
                          in {r.arrives_in_days > 0 ? `${r.arrives_in_days}d ` : ""}{Math.round(r.arrives_in_hours % 24)}h
                        </span>
                      )}
                    </td>
                    <td>{r.flight_time||"—"}</td>
                    <td>{r.airline||"—"}</td>
                    <td>{r.flight_number||"—"}</td>
                    <td>{r.hotel_name||"—"}</td>
                    <td>
                      {r.hotel_pickup_time||"—"}
                      {r.pickup_unfilled_warning && (
                        <div data-testid={`arr-unfilled-${r.id}`}
                             className="mt-1 text-[10px] px-1.5 py-0.5 bg-red-600 text-white rounded font-bold whitespace-nowrap">
                          ⚠ Unfilled pickup
                        </div>
                      )}
                    </td>
                    {(() => {
                      // Pickup cell is now visible to EVERYONE — BDOM,
                      // PCWM, GEHM, Founder — so the whole team sees the
                      // same "Pickup by NAME" stamp. Editable branch below
                      // is still gated to Founder + GEHM via `canAssign`.
                      const empty = !r.pickup_mode && !r.pickup_name && !r.assigned_user_id;
                      if (!canAssign && empty) {
                        return (
                          <td className="min-w-[220px] text-xs text-[color:var(--aii-muted)] italic">
                            Not yet assigned
                          </td>
                        );
                      }
                      return (
                      <td className="min-w-[240px]">
                        {(() => {
                          // Resolve who "self" pickup is actually assigned to
                          // (was hard-coded to the current viewer's name —
                          // Founder saw "Self (Rahul)" even when GEHM had
                          // assigned themselves).
                          const selfAssignee = r.pickup_mode === "self" && r.assigned_user_id
                            ? (team.find(t => t.id === r.assigned_user_id)
                               || (user.id === r.assigned_user_id ? user : null))
                            : null;
                          const isFilled = (r.pickup_mode === "self" && r.assigned_user_id) ||
                                           (r.pickup_mode === "other" && r.pickup_name);
                          // Locked when the row already has a pickup AND
                          // the viewer isn't allowed to change assignments
                          // (BDOM / PCWM / participants of routes). Founder
                          // + GEHM (canAssign) stay in the editable branch.
                          const locked = isFilled && !canAssign;
                          if (locked) {
                            return (
                              <div data-testid={`arr-pickup-locked-${r.id}`}
                                   className="text-xs bg-slate-50 border border-[color:var(--aii-border)] rounded p-2">
                                <div className="text-[9px] uppercase tracking-widest text-brand font-bold flex items-center gap-1">
                                  <User size={9}/> Pickup by
                                </div>
                                <div className="font-semibold text-sm mt-0.5" data-testid={`arr-pickup-name-${r.id}`}>
                                  {r.pickup_mode === "self"
                                    ? (selfAssignee?.name || "Team member")
                                    : r.pickup_name}
                                </div>
                                {r.pickup_mode === "other" && r.pickup_phone && (
                                  <div className="text-[10px] text-[color:var(--aii-muted)]">{r.pickup_phone}</div>
                                )}
                                <div className="text-[10px] text-[color:var(--aii-muted)] uppercase tracking-widest mt-1">
                                  {r.pickup_mode === "self" ? "Aii team" : "External"} · locked
                                </div>
                                <PickupAudit row={r}/>
                              </div>
                            );
                          }
                          return (
                            <>
                              <select data-testid={`arr-mode-${r.id}`} value={r.pickup_mode||""}
                                onChange={e=>patch(r.id, {
                                  pickup_mode: e.target.value,
                                  // Stamp current user's id when picking "self"
                                  // inline — previously we only recorded the
                                  // mode and left `assigned_user_id` null,
                                  // which broke both the display AND the
                                  // auto-generated pickup task on the
                                  // assignee's dashboard.
                                  ...(e.target.value === "self" ? { assigned_user_id: user.id } : {}),
                                  pickup_name: e.target.value==="self"?"":r.pickup_name,
                                  pickup_phone: e.target.value==="self"?"":r.pickup_phone,
                                })}
                                className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-xs mb-1 w-full">
                                <option value="">— assign —</option>
                                <option value="self">Self · assign to me</option>
                                <option value="other">Others (external)</option>
                              </select>
                              {r.pickup_mode === "self" && r.assigned_user_id && (
                                <div className="text-[10px] text-[color:var(--aii-muted)] flex items-center gap-1 mb-1"
                                     data-testid={`arr-self-current-${r.id}`}>
                                  <User size={9}/> Currently:&nbsp;<b className="text-[color:var(--aii-text-2)]">{selfAssignee?.name || "Team member"}</b>
                                  {canAssign && (
                                    <button data-testid={`arr-pickup-clear-${r.id}`}
                                      onClick={() => patch(r.id, { pickup_mode: "", assigned_user_id: null, pickup_name: "", pickup_phone: "" })}
                                      className="ml-auto text-red-600 hover:underline font-semibold">clear</button>
                                  )}
                                </div>
                              )}
                              {r.pickup_mode === "other" && (
                                <div className="flex flex-col gap-1 mt-1">
                                  <input data-testid={`arr-pname-${r.id}`} placeholder="Person's name" defaultValue={r.pickup_name||""}
                                    onBlur={e=>patch(r.id, { pickup_name: e.target.value })}
                                    className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-xs" />
                                  <input data-testid={`arr-pphone-${r.id}`} placeholder="WhatsApp # (with country code)" defaultValue={r.pickup_phone||""}
                                    onBlur={e=>patch(r.id, { pickup_phone: e.target.value })}
                                    className="px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-xs" />
                                  <div className="flex items-center gap-1">
                                    <button data-testid={`arr-save-${r.id}`}
                                      onClick={() => {
                                        const nameEl = document.querySelector(`[data-testid="arr-pname-${r.id}"]`);
                                        const phoneEl = document.querySelector(`[data-testid="arr-pphone-${r.id}"]`);
                                        patch(r.id, {
                                          pickup_name: nameEl?.value || "",
                                          pickup_phone: phoneEl?.value || "",
                                        });
                                        flashRow(r.id);
                                        toast.success("Pickup saved");
                                      }}
                                      title="Save this pickup assignment"
                                      className="text-xs bg-brand text-white px-2 py-1 rounded flex items-center justify-center gap-1 font-semibold flex-1">
                                      <Check size={11}/> Save
                                    </button>
                                    <button data-testid={`arr-edit-${r.id}`}
                                      onClick={() => {
                                        const nameEl = document.querySelector(`[data-testid="arr-pname-${r.id}"]`);
                                        nameEl?.focus?.();
                                      }}
                                      title="Edit this pickup assignment"
                                      className="text-xs border border-[color:var(--aii-border)] text-[color:var(--aii-text-2)] px-2 py-1 rounded hover:bg-slate-50 flex items-center justify-center gap-1 font-semibold">
                                      <Pencil size={11}/> Edit
                                    </button>
                                    {canAssign && (
                                      <button data-testid={`arr-pickup-del-${r.id}`}
                                        onClick={() => patch(r.id, { pickup_mode: "", assigned_user_id: null, pickup_name: "", pickup_phone: "" })}
                                        title="Clear this pickup assignment (Founder + GEHM)"
                                        className="text-xs border border-red-300 text-red-600 px-2 py-1 rounded hover:bg-red-50 flex items-center justify-center gap-1 font-semibold">
                                        <Trash2 size={11}/>
                                      </button>
                                    )}
                                  </div>
                                  <button data-testid={`arr-wa-${r.id}`} onClick={()=>sendWA(r)}
                                    className="text-xs bg-[#25D366] hover:bg-[#1EBE5D] text-white px-2 py-1 rounded flex items-center justify-center gap-1 font-semibold">
                                    <MessageCircle size={11}/> Send WhatsApp
                                  </button>
                                </div>
                              )}
                              <PickupAudit row={r}/>
                            </>
                          );
                        })()}
                      </td>
                      );
                    })()}
                    <td className="text-right">
                      <button data-testid={`arr-history-${r.id}`}
                        onClick={() => setHistoryTarget(r)}
                        title="View full edit history"
                        className="text-[color:var(--aii-muted)] hover:text-brand p-1 mr-1">
                        <History size={14}/>
                      </button>
                      {canDelete && <button data-testid={`arr-del-${r.id}`} onClick={()=>setDelTarget(r)} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>}
                    </td>
                  </tr>
                );

                if (groupBy === "none") {
                  for (const r of visibleRows) out.push(rowJsx(r));
                  return out;
                }

                if (groupBy === "month") {
                  const byMonth = new Map();
                  for (const r of visibleRows) {
                    const k = (r.flight_date || "").slice(0, 7) || "No date";
                    if (!byMonth.has(k)) byMonth.set(k, []);
                    byMonth.get(k).push(r);
                  }
                  for (const [ym, list] of [...byMonth.entries()].sort(([a],[b]) => a.localeCompare(b))) {
                    const key = `month:${ym}`;
                    const isCollapsed = collapsedGroups.has(key);
                    out.push(
                      <tr key={`hdr-m-${ym}`} data-testid={`arr-month-hdr-${ym}`}
                          className="bg-[color:var(--aii-green-tint)]/60 cursor-pointer hover:bg-[color:var(--aii-green-tint)]"
                          onClick={() => toggleGroup(key)}>
                        <td colSpan={cols} className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-brand select-none">
                          {isCollapsed ? "▸" : "▾"} {ym} · {list.length} arrival{list.length === 1 ? "" : "s"}
                        </td>
                      </tr>
                    );
                    if (isCollapsed) continue;
                    for (const r of list) out.push(rowJsx(r));
                  }
                  return out;
                }

                // Default: city → date two-level grouping (driver-roster view).
                // Prefer arrival_airport (where they LAND) over placement city
                // so a Delhi-airport arrival on a Jaipur placement clusters
                // under Delhi where the pickup driver actually collects them.
                const arrCityKey = (r) => normalizeArrivalCity(r.arrival_airport || r.city || "") || "Unassigned city";
                const byCity = new Map();
                for (const r of visibleRows) {
                  const c = arrCityKey(r);
                  if (!byCity.has(c)) byCity.set(c, new Map());
                  const byDate = byCity.get(c);
                  const d = (r.flight_date || "").slice(0, 10) || "No date";
                  if (!byDate.has(d)) byDate.set(d, []);
                  byDate.get(d).push(r);
                }
                for (const [city, byDate] of [...byCity.entries()].sort(([a],[b]) => a.localeCompare(b))) {
                  const cityKey = `city:${city}`;
                  const cityCollapsed = collapsedGroups.has(cityKey);
                  const cityTotal = [...byDate.values()].reduce((n, l) => n + l.length, 0);
                  out.push(
                    <tr key={`hdr-${city}`} data-testid={`arr-city-hdr-${city}`}
                        className="bg-[color:var(--aii-green-tint)]/60 cursor-pointer hover:bg-[color:var(--aii-green-tint)]"
                        onClick={() => toggleGroup(cityKey)}>
                      <td colSpan={cols} className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-brand select-none">
                        {cityCollapsed ? "▸" : "▾"} {city} · {cityTotal} arrival{cityTotal === 1 ? "" : "s"}
                      </td>
                    </tr>
                  );
                  if (cityCollapsed) continue;
                  for (const [d, list] of [...byDate.entries()].sort(([a],[b]) => a.localeCompare(b))) {
                    const dateKey = `date:${city}:${d}`;
                    const dateCollapsed = collapsedGroups.has(dateKey);
                    out.push(
                      <tr key={`sub-${city}-${d}`} data-testid={`arr-date-hdr-${city}-${d}`}
                          className="bg-[color:var(--aii-green-tint)]/25 cursor-pointer hover:bg-[color:var(--aii-green-tint)]/40"
                          onClick={() => toggleGroup(dateKey)}>
                        <td colSpan={cols} className="pl-8 pr-3 py-1 text-[11px] font-semibold text-[color:var(--aii-text-2)] select-none">
                          {dateCollapsed ? "▸" : "▾"} {d} · {list.length} arrival{list.length === 1 ? "" : "s"}
                        </td>
                      </tr>
                    );
                    if (dateCollapsed) continue;
                    for (const r of list) out.push(rowJsx(r));
                  }
                }
                return out;
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {canAssign && selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-brand text-white rounded-full shadow-2xl px-4 py-2.5 flex items-center gap-3 z-40" data-testid="arr-bulk-bar">
          <span className="text-sm font-semibold" data-testid="arr-bulk-count">{selected.size} selected</span>
          <span className="text-white/50">·</span>
          <button data-testid="arr-bulk-open" onClick={() => setBulkOpen(true)}
                  className="text-sm font-semibold px-3 py-1 rounded bg-white text-brand hover:bg-slate-50 flex items-center gap-1">
            <Users size={12}/> Assign pickup
          </button>
          <button data-testid="arr-bulk-clear" onClick={clearSelected}
                  className="text-xs text-white/70 hover:text-white p-1"><X size={13}/></button>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setBulkOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="arr-bulk-modal">
            <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-2">
              <div>
                <h3 className="font-display text-lg font-semibold">Assign pickup</h3>
                <p className="text-xs text-[color:var(--aii-muted)]">One pickup will be applied to <b>{selected.size}</b> arrival{selected.size === 1 ? "" : "s"}.</p>
              </div>
              <button onClick={() => setBulkOpen(false)} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <button data-testid="arr-bulk-mode-self" onClick={() => setBulkMode("self")}
                        className={`flex-1 text-xs px-3 py-1.5 rounded-md border font-semibold ${bulkMode === "self" ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)] hover:bg-slate-50"}`}>
                  Self (team member)
                </button>
                <button data-testid="arr-bulk-mode-other" onClick={() => setBulkMode("other")}
                        className={`flex-1 text-xs px-3 py-1.5 rounded-md border font-semibold ${bulkMode === "other" ? "bg-brand text-white border-brand" : "bg-white border-[color:var(--aii-border)] hover:bg-slate-50"}`}>
                  External driver
                </button>
              </div>
              {bulkMode === "self" ? (
                <label className="block text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Team member
                  <select data-testid="arr-bulk-user" value={bulkForm.user_id}
                          onChange={e => setBulkForm(f => ({ ...f, user_id: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                    <option value="">— pick a driver —</option>
                    {drivers.map(u => (
                      <option key={u.id} value={u.id}>{u.name} ({u.role}{u.city ? ` · ${u.city}` : ""})</option>
                    ))}
                  </select>
                </label>
              ) : (
                <div className="space-y-2">
                  <label className="block text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">Person&apos;s name
                    <input data-testid="arr-bulk-name" value={bulkForm.pickup_name}
                           onChange={e => setBulkForm(f => ({ ...f, pickup_name: e.target.value }))}
                           placeholder="e.g. Ramesh Meena"
                           className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
                  </label>
                  <label className="block text-[11px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">WhatsApp # (with country code)
                    <input data-testid="arr-bulk-phone" value={bulkForm.pickup_phone}
                           onChange={e => setBulkForm(f => ({ ...f, pickup_phone: e.target.value }))}
                           placeholder="e.g. 919812345678"
                           className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
                  </label>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
              <button onClick={() => setBulkOpen(false)} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
              <button data-testid="arr-bulk-apply" onClick={applyBulkPickup} disabled={bulkBusy}
                      className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1">
                <Users size={13}/> {bulkBusy ? "Assigning…" : `Apply to ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <DeleteConfirmDialog
        open={!!delTarget}
        title="Delete this arrival?"
        description="The arrival record will be removed. Restore any time from the Recycle Bin."
        label={delTarget ? `${delTarget.customer || "Unknown"} · ${delTarget.airline || ""} ${delTarget.flight_number || ""} · ${delTarget.flight_date || ""}` : ""}
        onCancel={() => setDelTarget(null)}
        onConfirm={async () => { await del(delTarget.id); setDelTarget(null); }}
      />
      {pendingUpload && (
        <UploadPreviewModal file={pendingUpload} endpoint="/arrivals/upload"
          label="arrivals"
          onClose={() => setPendingUpload(null)}
          onDone={load}/>
      )}
      {historyTarget && (
        <ArrivalHistoryModal arrival={historyTarget} onClose={() => setHistoryTarget(null)}/>
      )}
    </div>
  );
}
